import React from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import './audioPlayer.css';
import { useAppSelector } from '../../redux/hooks';
import { useDispatch } from 'react-redux';
import { playbackStatusSelector, mutedSelector, play, autoplaySelector } from '../../redux/slices/ui';
import DynamicText from './DynamicText';
import Overlay from './Overlay';
const Video = ({ src, trackSrc, dynamicTextSrc, audioOnly, onEnded, onCueChange, }) => {
    const dispatch = useDispatch();
    const videoRef = React.useRef(null);
    const playerRef = React.useRef(null);
    const dtTrackRef = React.useRef(null);
    const playbackStatus = useAppSelector(playbackStatusSelector);
    const muted = useAppSelector(mutedSelector);
    const autoPlay = useAppSelector(autoplaySelector);
    // this forces the component to reload when the player is ready
    // for access to the texttracks
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [playerReady, setPlayerReady] = React.useState(false);
    if (playbackStatus === 'playing' && !playerRef.current?.ended()) {
        void playerRef.current?.play();
    }
    else {
        playerRef.current?.pause();
    }
    playerRef.current?.muted(muted);
    React.useEffect(() => {
        // Make sure Video.js player is only initialized once
        if (!playerRef.current) {
            // The Video.js player needs to be _inside_ the component el for React 18 Strict Mode.
            const videoElement = document.createElement('video-js');
            const options = {
                autoplay: autoPlay,
                playsinline: audioOnly ? undefined : true,
                controls: false,
                responsive: true,
                fluid: audioOnly ? undefined : true,
                aspectRatio: audioOnly ? undefined : '16:9',
                muted,
                html5: {
                    nativeTextTracks: false,
                },
                audioOnly: audioOnly ? true : false,
                sources: [
                    {
                        src: src,
                        type: audioOnly ? 'audio/mp3' : 'video/mp4',
                    },
                ],
                bigPlayButton: false
            };
            videoElement.classList.add('vjs-big-play-centered');
            videoElement.setAttribute('disableremoteplayback', 'true');
            videoRef.current?.appendChild(videoElement);
            playerRef.current = videojs(videoElement, options);
            playerRef.current.on('ended', onEnded);
            playerRef.current.on('ready', () => {
                setPlayerReady(true);
                const player = playerRef.current;
                if (!player) {
                    return;
                }
                addClosedCaptions(player, trackSrc, onCueChange);
                if (dynamicTextSrc) {
                    const trackEl = player.addRemoteTextTrack({
                        kind: 'metadata',
                        src: dynamicTextSrc,
                        label: 'English',
                        srclang: 'en',
                        mode: 'showing',
                    });
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    dtTrackRef.current = trackEl.track;
                }
            });
        }
    }, [muted, src, trackSrc, dynamicTextSrc, audioOnly, onEnded, onCueChange]);
    // this second effect will only fire in audio-only moments with multiple audio files, or response audio.
    React.useEffect(() => {
        if (playerReady && audioOnly && playerRef.current) {
            const player = playerRef.current;
            player.src({
                src: src,
                type: 'audio/mp3',
            });
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const track = player.remoteTextTracks()[0];
            player.removeRemoteTextTrack(track);
            addClosedCaptions(player, trackSrc, onCueChange);
            player.on('ended', onEnded);
            void player.play();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src, trackSrc]);
    // Dispose the Video.js player when the functional component unmounts
    React.useEffect(() => {
        const player = playerRef.current;
        playerRef.current?.on('play', () => {
            dispatch(play());
        });
        return () => {
            if (player && !player.isDisposed()) {
                player.dispose();
                playerRef.current = null;
            }
        };
    }, [playerRef]);
    return (React.createElement("div", null,
        dtTrackRef.current && (React.createElement(DynamicText, { track: dtTrackRef.current, hasStarted: true })),
        playbackStatus === 'paused' && React.createElement(Overlay, null),
        audioOnly && (React.createElement("div", { className: "vjs-audio-only" },
            React.createElement("div", { ref: videoRef }))),
        !audioOnly && (React.createElement("div", { "data-vjs-player": true },
            React.createElement("div", { ref: videoRef })))));
};
function addClosedCaptions(player, trackSrc, onCueChange) {
    const trackEl = player.addRemoteTextTrack({
        kind: 'captions',
        src: trackSrc,
        label: 'English',
        srclang: 'en',
        mode: 'hidden',
    });
    // Something is wonky with the Typescrit types for HTMLTrackElement
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const textTrack = trackEl.track;
    textTrack.addEventListener('cuechange', () => {
        const cueList = textTrack.activeCues;
        const captions = cueList ? extractTextFromCueList(cueList) : [];
        onCueChange(captions);
    });
}
function extractTextFromCueList(cueList) {
    const lines = [];
    for (let i = 0; i < cueList.length; i++) {
        const cue = cueList[i];
        const textContent = cue.getCueAsHTML().textContent;
        if (textContent) {
            lines.push(textContent);
        }
    }
    return lines;
}
export default Video;
//# sourceMappingURL=Video.js.map